/**
 * api-endpoints.tsx
 * ──────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for every backend URL used by the SK Platform console.
 *
 * Backend service:
 *   PLATFORM_API → skplatform-api  (identity/auth, orgs, members, invitations,
 *                  roles, licensing/plans/subscriptions/usage, billing, admin)
 *
 * Rules (mirrors sk-catalog conventions):
 *   - Base URLs come ONLY from process.env — injected at web build time via
 *     webpack DefinePlugin (see web/webpack.config.js) and at native build time
 *     via babel inline-dotenv / the .env file. NEVER hardcode a base URL.
 *   - Zero hardcoded URLs anywhere else in the codebase. Screens call services;
 *     services import the consts below.
 *   - To switch environments, change ONLY the .env values (DevOps-managed).
 *
 * Every path is sourced from skplatform-api/00-vision (Section A — Platform API).
 * ──────────────────────────────────────────────────────────────────────────
 */

// ── Base URLs (env-driven, localhost fallback for dev) ─────────────────────
export const PLATFORM_API =
  process.env.PLATFORM_API_URL || 'http://localhost:3013'; // skplatform-api local port (see skplatform-api/.env PORT=3013)

// Versioned prefixes
const P = `${PLATFORM_API}/v1`; // tenantId-facing platform v1
const A = `${PLATFORM_API}/admin/v1`; // platform-admin console v1

/* ═══════════════════════════════════════════════════════════════════════════
   AUTH & IDENTITY  (httpOnly cookie session — see axiosInstance)
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Signup_Url = `${P}/auth/signup`; // POST { email, password, name, org_name, country, gst_number, product }
export const Platform_Login_Url = `${P}/auth/login`; // POST { email, password } → { user, org, tokens } | { kind:'mfa_required', mfa_token }
export const Platform_Login_Mfa_Url = `${P}/auth/login/mfa`; // POST { mfa_token, code }
export const Platform_Refresh_Token_Url = `${P}/auth/refresh`; // POST — silent rotation (httpOnly cookie)
export const Platform_Logout_Url = `${P}/auth/logout`; // POST (204)
export const Platform_Me_Url = `${P}/me`; // GET — identity probe ({ user, org, memberships, products_entitled })
export const Platform_Update_Me_Url = `${P}/me`; // PATCH { name?, email? }
export const Platform_Forgot_Password_Url = `${P}/auth/forgot-password`; // POST { email } (204)
export const Platform_Reset_Password_Url = `${P}/auth/reset-password`; // POST { token, new_password } (204)
export const Platform_Reset_Password_Claim_Url = `${P}/auth/reset-password/claim`; // POST { token } → { ok, grant? } — consumes the emailed link (single-open) and returns an in-memory grant the form submits with
export const Platform_Verify_Email_Url = `${P}/auth/verify-email`; // POST { token }
export const Platform_Verify_Stream_Url = `${P}/auth/verify-stream`; // GET (SSE) — pushes a 'verified' event when the email is confirmed (replaces /me polling)
export const Platform_Resend_Verification_Url = `${P}/auth/resend-verification`; // POST { product } (204, authenticated)
export const Platform_Switch_Org_Url = `${P}/auth/switch-org`; // POST { org_id }
export const Platform_Accept_Invitation_Url = `${P}/auth/accept-invitation`; // POST { token, password?, first_name?, last_name?, country_code?, phone_number? }
export const Platform_Invites_Accept_Url = `${P}/invites/accept`; // POST { token }
export const Platform_Invites_Decline_Url = `${P}/invites/decline`; // POST { token }
export const Platform_My_Invitations_Url = `${P}/me/invitations`; // GET — pending invites for me
export const Platform_Invite_Preview_Url = (token: string) => `${P}/invites/${encodeURIComponent(token)}/preview`; // GET, public — { email, org_name, role, product, status, user_exists }

/* ── Centralized auth — cross-product redirect ─────────────────────────────
   Public allow-list of product FE origins, derived server-side from the product
   registry. Used to validate a `return_to` target before redirecting a
   freshly-authenticated user back into the originating product (open-redirect
   guard). See navigation/productHandoff + config/products. */
export const Platform_Redirect_Targets_Url = `${P}/products/redirect-targets`; // GET → { data: { targets: { slug: origin } } }

/* ── MFA (TOTP) ──────────────────────────────────────────────────────────── */
export const Platform_Mfa_Enroll_Url = `${P}/me/mfa/totp/enroll`; // POST → { secret, otpauth_url }
export const Platform_Mfa_Verify_Enroll_Url = `${P}/me/mfa/totp/verify-enrollment`; // POST { code } → { recovery_codes[] }
export const Platform_Mfa_Disable_Url = `${P}/me/mfa/totp/disable`; // POST { code }
export const Platform_Mfa_Recovery_Regen_Url = `${P}/me/mfa/recovery-codes/regenerate`; // POST { code } → { recovery_codes[] }

/* ── Passkeys / WebAuthn ─────────────────────────────────────────────────── */
export const Platform_Webauthn_Register_Options_Url = `${P}/me/webauthn/register/options`; // POST
export const Platform_Webauthn_Register_Verify_Url = `${P}/me/webauthn/register/verify`; // POST { device_label, response }
export const Platform_Webauthn_Credentials_Url = `${P}/me/webauthn/credentials`; // GET
export const Platform_Webauthn_Credential_Url = (id: string) =>
  `${P}/me/webauthn/credentials/${id}`; // DELETE

/* ═══════════════════════════════════════════════════════════════════════════
   ORGANIZATION & TEAM
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Orgs_Url = `${P}/orgs`; // POST — create org
export const Platform_Org_Url = (id: string) => `${P}/orgs/${id}`; // GET | PATCH
export const Platform_Org_Members_Url = (id: string) => `${P}/orgs/${id}/members`; // GET
export const Platform_Org_Member_Url = (id: string, userId: string) =>
  `${P}/orgs/${id}/members/${userId}`; // DELETE
export const Platform_Org_Member_Roles_Url = (id: string, userId: string) =>
  `${P}/orgs/${id}/members/${userId}/roles`; // GET | POST { role_id }
export const Platform_Org_Member_Role_Url = (id: string, userId: string, role: string) =>
  `${P}/orgs/${id}/members/${userId}/roles/${role}`; // DELETE
export const Platform_Org_Invitations_Url = (id: string) => `${P}/orgs/${id}/invitations`; // GET | POST { email, role, products[] }
export const Platform_Org_Invitation_Url = (id: string, invId: string) =>
  `${P}/orgs/${id}/invitations/${invId}`; // DELETE
export const Platform_Org_Invitation_Resend_Url = (id: string, invId: string) =>
  `${P}/orgs/${id}/invitations/${invId}/resend`; // POST
/* ── Domains (SKPLATFORM-CC-002 CH-1 / UICC CH-U1) ──────────────────────────
   A bound domain is how a rider's browser reaches an operator. DELETE is the
   verb, but the effect is a DEACTIVATION — the row survives with its
   deactivated_at stamp and still appears under ?include_inactive=true. There is
   no delete anywhere in this feature. */
export const Platform_Org_Hosts_Url = (id: string) => `${P}/orgs/${id}/hosts`; // GET ?include_inactive | POST { host }
export const Platform_Org_Host_Url = (id: string, hostId: string) =>
  `${P}/orgs/${id}/hosts/${hostId}`; // DELETE — deactivates, never removes

export const Platform_Org_External_Users_Url = (id: string) =>
  `${P}/orgs/${id}/external-users`; // GET
export const Platform_Org_External_User_Url = (id: string, userId: string) =>
  `${P}/orgs/${id}/external-users/${userId}`; // DELETE

/* ═══════════════════════════════════════════════════════════════════════════
   LICENSING — PLANS, SUBSCRIPTIONS, TRIALS, USAGE, WALLETS
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Plans_Url = `${P}/plans`; // GET — public plan catalog
export const Platform_Org_Subscription_Url = (id: string) =>
  `${P}/orgs/${id}/subscription`; // GET | POST { plan_id, payment_method_token? }
export const Platform_Org_Subscription_Cancel_Url = (id: string) =>
  `${P}/orgs/${id}/subscription:cancel`; // POST
export const Platform_Org_Subscription_ChangePlan_Url = (id: string, subId: string) =>
  `${P}/orgs/${id}/subscriptions/${subId}/change-plan`; // POST { plan_id }
export const Platform_Org_Products_Url = (id: string) => `${P}/orgs/${id}/products`; // GET
export const Platform_Org_Product_Url = (id: string, product: string) =>
  `${P}/orgs/${id}/products/${product}`; // POST | PATCH | DELETE { quantity }
export const Platform_Org_Product_StartTrial_Url = (id: string, product: string) =>
  `${P}/orgs/${id}/products/${product}/start-trial`; // POST
export const Platform_Org_Trials_Url = (id: string) => `${P}/orgs/${id}/trials`; // GET
export const Platform_Org_Trial_Convert_Url = (id: string, productId: string) =>
  `${P}/orgs/${id}/trials/${productId}/convert`; // POST
export const Platform_Org_Usage_Url = (id: string) => `${P}/orgs/${id}/usage`; // GET — used vs limit by metric
export const Platform_Org_Usage_Records_Url = (id: string) =>
  `${P}/orgs/${id}/usage-records`; // GET

export const Platform_Org_Wallets_Url = (id: string) => `${P}/orgs/${id}/wallets`; // GET
export const Platform_Org_Wallet_Recharge_Url = (id: string, product: string) =>
  `${P}/orgs/${id}/wallets/${product}/recharge`; // POST { units }
export const Platform_Org_Wallet_Settings_Url = (id: string, product: string) =>
  `${P}/orgs/${id}/wallets/${product}/settings`; // PATCH

/* ═══════════════════════════════════════════════════════════════════════════
   BILLING — PAYMENT METHODS & INVOICES (Razorpay)
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Org_PaymentMethods_Url = (id: string) =>
  `${P}/orgs/${id}/payment-methods`; // GET | POST → { form_url }
export const Platform_Org_PaymentMethod_Url = (id: string, pmId: string) =>
  `${P}/orgs/${id}/payment-methods/${pmId}`; // DELETE
export const Platform_Org_Invoices_Url = (id: string) => `${P}/orgs/${id}/invoices`; // GET (cursor)
export const Platform_Org_Invoice_Url = (id: string, invoiceId: string) =>
  `${P}/orgs/${id}/invoices/${invoiceId}`; // GET
export const Platform_Org_Invoice_Pdf_Url = (id: string, invoiceId: string) =>
  `${P}/orgs/${id}/invoices/${invoiceId}/pdf`; // GET (application/pdf)

/* ═══════════════════════════════════════════════════════════════════════════
   AUDIT
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Audit_Log_Url = `${P}/audit-log`; // GET ?actor_id&action&entity&from&to&cursor&limit

/* ═══════════════════════════════════════════════════════════════════════════
   PRIVACY (DPDP) — long-running operations
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Operation_Url = (id: string) => `${P}/operations/${id}`; // GET — job status

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM ADMIN CONSOLE
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Admin_Orgs_Url = `${A}/orgs`; // GET ?query&limit
export const Platform_Admin_Org_BillingMode_Url = (id: string) =>
  `${A}/orgs/${id}/billing-mode`; // PATCH { billing_mode }
export const Platform_Admin_Org_Credit_Url = (id: string) => `${A}/orgs/${id}/credit`; // POST
export const Platform_Admin_Org_Notes_Url = (id: string) => `${A}/orgs/${id}/notes`; // POST

/* ── Flagged coverage gaps (00-vision does not yet define these; the screens
      below call typed service stubs so backend can confirm/add the routes):
        • Admin Applications CRUD  — Platform_Admin_Applications_*_Url
        • Admin Plan authoring     — Platform_Admin_Plans_*_Url
      They are namespaced under /admin/v1 to match the admin convention. ── */
export const Platform_Admin_Applications_Url = `${A}/applications`; // GET | POST  (STUB — confirm)
export const Platform_Admin_Application_Url = (id: string) => `${A}/applications/${id}`; // GET|PATCH|DELETE (STUB)
export const Platform_Admin_Plans_Url = `${A}/plans`; // GET | POST  (STUB — authoring)
export const Platform_Admin_Plan_Url = (id: string) => `${A}/plans/${id}`; // GET|PATCH|DELETE (STUB)
export const Platform_Admin_Plan_Publish_Url = (id: string) => `${A}/plans/${id}/publish`; // POST (STUB)

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM OPS — OUTBOX DEAD-LETTER QUEUE (CC-002 CH-6.2 / UICC CH-U5)
   platform_admin only. There is deliberately NO delete endpoint: a failed row
   is the only record that an event should have been delivered and was not, so
   the console can requeue it but never destroy it.
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Outbox_Dlq_Url = `${P}/internal/outbox/dlq`; // GET ?limit — oldest-unresolved first
export const Platform_Outbox_Requeue_Url = (id: string) =>
  `${P}/internal/outbox/${id}/requeue`; // POST — returns the event to PENDING

/* ═══════════════════════════════════════════════════════════════════════════
   OPERATIONAL
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Health_Url = `${PLATFORM_API}/health`; // GET — unauthenticated

/* ═══════════════════════════════════════════════════════════════════════════
   SKPLATFORM-CC-006 / UICC-006 — residency, key custody, ledgers, customer
   access.

   Three of these are INTERNAL ONLY: the signing key register, the RLS bypass
   ledger and realm-mismatch events. Putting the bypass ledger or the key
   register on a tenant-facing screen would be a finding, not a feature, so
   they live under the admin prefix and are service/admin gated server-side.

   NOTE ON THE TWO LEDGERS. `identity.rls_bypass_audit` and
   `identity.realm_mismatch_event` are INSERT-only for the runtime role by
   design — an audit trail the audited party can read is a weaker audit trail.
   So these two read endpoints require a separate REPORTING role on the API
   side, which does not exist yet (raised as a gap in the CR status). Until it
   does, the screens render their empty state, which is why that empty state
   is written to read as information rather than as an error.
═══════════════════════════════════════════════════════════════════════════ */

/** Closed vocabulary of residency zones — the provisioning select (C-2). */
export const Platform_Residency_Zones_Url = `${A}/residency-zones`; // GET

/** N-2 · signing key register. Public JWK material and KMS paths only. */
export const Platform_Admin_Signing_Keys_Url = `${A}/signing-keys`; // GET

/** N-3 · RLS bypass ledger, and the per-call-site rollup the screen leads with. */
export const Platform_Admin_Rls_Bypass_Url = `${A}/rls-bypass`; // GET ?limit
export const Platform_Admin_Rls_Bypass_Sites_Url = `${A}/rls-bypass/by-site`; // GET

/** N-4 · realm mismatch events. Every row is a candidate security incident. */
export const Platform_Admin_Realm_Mismatch_Url = `${A}/realm-mismatch`; // GET ?since

/** N-5 · audience registry. Read-mostly; adding a consumer is a seed row. */
export const Platform_Admin_Audiences_Url = `${A}/audiences`; // GET

/** N-6 · customer activity across tenants — the pre-pricing sanity check. */
export const Platform_Admin_Customer_Activity_Url = `${A}/customer-activity`; // GET ?period

/** N-1 · the tenant's own figure and access list. */
export const Platform_Org_Customer_Activity_Url = (id: string) =>
  `${P}/orgs/${id}/customer-activity`; // GET — current period
export const Platform_Org_Customer_Access_Url = (id: string) =>
  `${P}/orgs/${id}/customer-access`; // GET ?status&sort=last_seen
export const Platform_Org_Customer_Access_Revoke_Url = (id: string) =>
  `${P}/orgs/${id}/customer-access/revoke`; // POST — bulk revoke
